/*
 * tc-collection-status.js: accessibility companion for the collection product grid
 * (issue #455, also closes #450). WCAG 2.2 SC 4.1.3 Status Messages, SC 4.1.2 Name,
 * Role, Value, SC 2.4.3 Focus Order.
 *
 * Additive and isolated. Does NOT modify assets/theme.js (a minified bundle with no
 * source in this repo); same approach as predictive-search-dedup.js and
 * tc-card-swatch-select.js. Loaded by sections/collection.liquid and, since issue #716,
 * sections/search.liquid.
 *
 * The problem: theme.js FiltersForm.getFilterResult() re-renders the grid after every
 * filter, sort and page change by swapping the innerHTML of the filter panel
 * ([data-collection-sidebar]), the product grid ([data-products-grid], which also holds
 * the pagination), the active-filter chips and the count. refreshFunctionalities() then
 * builds a new Filters instance on the same <aside>, and its constructor hides the panel
 * (or, where the panel is shown by default on desktop, focuses its first control). So:
 *   - nothing announced the result; the "3 of 20 products" count changed silently;
 *   - the control that started the update was destroyed or hidden, and focus fell to
 *     <body>;
 *   - Sort.sortingResults moves only the .popout-list__item--current class, so the old
 *     sort option kept aria-current="true";
 *   - Toggle ("Show N products per row") marks the active button with .is-active only,
 *     so no pressed state reached assistive tech.
 *
 * What this does, per collection section:
 *   1. Announce. After each grid swap, copy the server-rendered summary from
 *      .js-grid[data-tc-results] ([data-tc-results-sorted] after a sort; both rendered by
 *      sections/collection.liquid) into the section's persistent role="status" region,
 *      [data-tc-results-status]. When focus is being returned (step 2), the text goes in
 *      right after the focus move, so the focus announcement cannot cut it off.
 *   2. Return focus, only when the update was started without a real mouse, pen or
 *      touch press (keyboard, or an assistive-tech default action) and focus was lost.
 *      Target: the control that had focus, by id, if it survived and is visible; else the
 *      Filter button (filter panel, chips, Clear filters), the Sort button (sort), or the
 *      first product link on the new page (pagination). After a page change the move
 *      waits (up to 3s) until that first product can take focus, because theme.js keeps
 *      new cards hidden for a moment. The Filter and Sort buttons are focused without
 *      scrolling when they are already on screen, so the page does not jump. Pointer
 *      users keep today's behaviour on purpose: the sitewide :focus ring in custom-css.css
 *      (F78 fallback) is unconditional, so moving their focus would paint a ring they
 *      never asked for. A real pointer press after the activation counts too.
 *      Overlapping updates (issue #666): theme.js never cancels a request, so a second
 *      change can start before the first response lands, and the responses can land in
 *      either order. Request starts are counted from the grid's .is-loading class, which
 *      theme.js sets as each request starts. Each activation notes how many starts must
 *      come before its own request is under way (its own, plus any earlier activation's
 *      that has not started yet; filter-panel inputs that theme.js's 1.5s debounce merges
 *      share one). The latest activation stays in force for every swap until its own
 *      request has started and nothing is left in flight. No timing assumption: a slow
 *      device that starts the request late still gets the focus return.
 *   3. Mirror state. aria-current follows .popout-list__item--current in the sort list;
 *      aria-pressed follows .is-active on the layout toggles.
 */
(function () {
  'use strict';

  if (window.__tcCollectionStatus) return;
  window.__tcCollectionStatus = true;

  var SECTION = '[data-section-type="collection"], [data-section-type="search-page"]';
  var GRID = '[data-products-grid]';
  var SUMMARY = '[data-tc-results]';
  var STATUS = '[data-tc-results-status]';
  var SIDEBAR = '[data-collection-sidebar]';
  var FILTER_TOGGLE = '[data-filters-toggle]';
  var SORT_LINK = '[data-sort-link]';
  var SORT_TOGGLE = '.collection-sort [data-popout-toggle]';
  var SORT_LIST = '.collection-sort [data-popout-list]';
  var UPDATE_LINK = '[data-filter-update-url]';
  var PAGINATION = '.pagination-custom';
  var GRID_TOGGLE = '[data-toggle-grid]';
  var PRODUCT_CARD = 'product-grid-item';
  var PRODUCT_LINK = 'a[href]:not([tabindex="-1"])';

  var CURRENT_SORT = 'popout-list__item--current'; // theme.js classes$8.active
  var PANEL_OPEN = 'drawer--visible';              // theme.js Filters classes$k.show
  var TOGGLE_ACTIVE = 'is-active';                 // theme.js Toggle classes$9.active
  var LOADING = 'is-loading';                      // theme.js FiltersForm classes$j.classLoading

  var POINTER_WINDOW = 1000; // ms: an activation this soon after a real pointer press came from it
  var TRIGGER_TTL = 30000;   // ms: a later update (back button, a slow response) is not attributed
  var INPUT_DEBOUNCE = 1500; // ms: theme.js FiltersForm debounces filter-panel input this long
  var SETTLE = 60;           // ms: run after theme.js's own 1ms showFilters() focus timer
  var ANNOUNCE_GAP = 150;    // ms: empty the region first so a repeated message is spoken again
  var READY_CAP = 3000;      // ms: how long to wait for the new page's first product to be focusable
  var READY_POLL = 100;      // ms

  var lastPointer = 0;
  var lastFocused = null;

  document.addEventListener('pointerdown', function (evt) {
    if (evt.isTrusted && evt.pointerType) lastPointer = Date.now();
  }, true);

  // The control that had focus when a swap destroyed it. focusin, not activeElement:
  // by the time the MutationObserver runs, the browser has already reset focus to body.
  document.addEventListener('focusin', function (evt) {
    lastFocused = evt.target;
  }, true);

  function isVisible(el) {
    // No opacity check: the filter checkboxes are transparent 0x0 inputs painted through
    // their labels, and they are still the right thing to focus.
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true });
    }
    if (el.getClientRects().length === 0) return false;
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.visibility !== 'collapse';
  }

  function usable(state, el) {
    if (!el || !el.isConnected || !state.section.contains(el) || el.disabled || !isVisible(el)) return false;
    // The panel is still painted while it animates closed (theme.js adds its display:none
    // class 800ms after hideFilters()), so ask the panel itself whether it is open.
    var sidebar = state.section.querySelector(SIDEBAR);
    return !(sidebar && sidebar.contains(el) && !sidebar.classList.contains(PANEL_OPEN));
  }

  function firstUsable(state, list, skipSidebar) {
    var sidebar = state.section.querySelector(SIDEBAR);
    for (var i = 0; i < list.length; i++) {
      if (skipSidebar && sidebar && sidebar.contains(list[i])) continue;
      if (usable(state, list[i])) return list[i];
    }
    return null;
  }

  // input: a filter-panel input event. theme.js debounces those into one request
  // (INPUT_DEBOUNCE after the last one), so an input inside that window shares the request of
  // the input before it. Past the window it may or may not share it (a slow device fires the
  // debounce late); counting it as its own request then only keeps the trigger a little
  // longer, while wrongly sharing would retire it before its own response.
  function record(state, el, kind, input) {
    var keyboardFocused = false;
    try { keyboardFocused = el.matches(':focus-visible'); } catch (e) { /* old engine */ }
    var now = Date.now();
    var prev = state.trigger;
    var waiting = prev && prev.starts > 0 && now - prev.time <= TRIGGER_TTL;
    var shared = input && waiting && prev.input && now - prev.time < INPUT_DEBOUNCE;
    state.trigger = {
      id: el.id || '',
      kind: kind,
      input: !!input,
      time: now,
      managed: keyboardFocused || now - lastPointer > POINTER_WINDOW,
      // Request starts still to come before this activation's own request is under way.
      starts: (waiting ? prev.starts : 0) + (shared ? 0 : 1)
    };
  }

  function classes(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function hasToken(value, token) {
    return (' ' + value + ' ').indexOf(' ' + token + ' ') !== -1;
  }

  // Whether class record i is theme.js starting a request: getFilterResult() adds .is-loading
  // to the grid first thing, and classList.add records a mutation even when the class is
  // already there (a second request while the first is loading). The value after record i is
  // the next class record's oldValue, or the live value for the last one. A record that keeps
  // .is-loading but changes other classes is someone else's edit, not a request.
  function startsRequest(records, i) {
    var after = records[i].target.getAttribute('class');
    for (var j = i + 1; j < records.length; j++) {
      if (records[j].type === 'attributes') { after = records[j].oldValue; break; }
    }
    var before = classes(records[i].oldValue);
    after = classes(after);
    return hasToken(after, LOADING) && (!hasToken(before, LOADING) || before === after);
  }

  function announce(state, trigger) {
    var region = state.section.querySelector(STATUS);
    var summary = state.grid.querySelector(SUMMARY);
    if (!region || !summary) return;
    // The search page (issue #716) merges its later result pages after the swap and keeps
    // the grid aria-busy until the final count is in. Hold the message until
    // assets/tc-search.js signals tc:results:ready, so an update never announces the page-1
    // count first.
    if (state.grid.getAttribute('aria-busy') === 'true') {
      state.pendingAnnounce = { trigger: trigger };
      return;
    }
    state.pendingAnnounce = null;
    var text = (trigger && trigger.kind === 'sort' && summary.getAttribute('data-tc-results-sorted')) ||
      summary.getAttribute('data-tc-results') || '';
    clearTimeout(state.announceTimer);
    region.textContent = '';
    state.announceTimer = setTimeout(function () { region.textContent = text; }, ANNOUNCE_GAP);
  }

  // themeMoved: the one element theme.js focused on its own right after the swap (see
  // afterUpdate). Any other element that holds focus was the shopper's choice.
  function focusLost(themeMoved) {
    var active = document.activeElement;
    if (!active || active === document.body || active === document.documentElement) return true;
    if (!active.isConnected || !isVisible(active)) return true;
    return active === themeMoved;
  }

  // Where focus belongs after this kind of update, or null when that control cannot take
  // focus yet.
  function preferredTarget(state, trigger) {
    var ids = [];
    if (state.prior && state.prior.id) ids.push(state.prior.id);
    if (trigger.id && ids.indexOf(trigger.id) === -1) ids.push(trigger.id);
    for (var i = 0; i < ids.length; i++) {
      var byId = document.getElementById(ids[i]);
      if (usable(state, byId)) return byId;
    }
    if (trigger.kind === 'sort') return firstUsable(state, state.section.querySelectorAll(SORT_TOGGLE));
    if (trigger.kind === 'page') {
      // The first card on the new page, never a later card or the pagination: theme.js
      // keeps new cards visibility:hidden for a while after the swap (the grid's
      // .is-loading state, then the card media), and anything else would win that race.
      var card = state.grid.querySelector(PRODUCT_CARD);
      return card ? firstUsable(state, card.querySelectorAll(PRODUCT_LINK)) : null;
    }
    return firstUsable(state, state.section.querySelectorAll(FILTER_TOGGLE), true);
  }

  function fallbackTarget(state) {
    return firstUsable(state, state.section.querySelectorAll(FILTER_TOGGLE), true) ||
      firstUsable(state, state.section.querySelectorAll(SORT_TOGGLE));
  }

  // The toolbar buttons are already on screen after an update (theme.js scrolls to the top of
  // the section first). Focusing them without scrolling keeps the page still: once the page
  // pads its scroll edge below the sticky header (issue #454), a plain focus() on a control
  // that sits in that padded band makes the browser scroll the page to re-centre it.
  function moveFocus(el) {
    var r = el.getBoundingClientRect();
    var onScreen = r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
    var toolbar = el.matches(FILTER_TOGGLE + ',' + SORT_TOGGLE);
    if (toolbar && onScreen) el.focus({ preventScroll: true });
    else el.focus();
  }

  function afterUpdate(state) {
    var trigger = state.trigger;
    if (trigger && Date.now() - trigger.time > TRIGGER_TTL) trigger = state.trigger = null;
    // This swap uses the trigger either way. It is retired only once its own request has
    // started and nothing is left in flight; until then a newer response is still to come.
    if (trigger && trigger.starts <= 0 && !state.inflight) state.trigger = null;
    if (!trigger || !trigger.managed) {
      announce(state, trigger);
      return;
    }
    // theme.js showFilters() focuses the panel's first control 1ms after the swap when the
    // panel reopens by default. The swap had just rebuilt the panel, so a panel control
    // holding focus now (SETTLE ms later) was put there by theme.js, not by the shopper.
    var active = document.activeElement;
    var sidebar = state.section.querySelector(SIDEBAR);
    var themeMoved = sidebar && active && sidebar.contains(active) &&
      !(state.prior && state.prior.id && active.id === state.prior.id) ? active : null;
    var deadline = Date.now() + READY_CAP;
    (function attempt() {
      // Re-checked on every try: the shopper may have moved on in the meantime, or pressed a
      // real mouse, pen or touch screen since the activation (then they are on a pointer now).
      if (lastPointer > trigger.time || !focusLost(themeMoved)) {
        announce(state, trigger);
        return;
      }
      var target = preferredTarget(state, trigger);
      if (!target && Date.now() < deadline) {
        state.retry = setTimeout(attempt, READY_POLL);
        return;
      }
      target = target || fallbackTarget(state);
      if (target) moveFocus(target);
      // After the focus move, not before: screen readers cut off speech in progress when
      // focus moves, so a message queued first could be lost (a11y-qa gate, PR #458, B2).
      announce(state, trigger);
    })();
  }

  function syncSort(section) {
    var links = section.querySelectorAll(SORT_LINK);
    for (var i = 0; i < links.length; i++) {
      var item = links[i].parentElement;
      if (item && item.classList.contains(CURRENT_SORT)) links[i].setAttribute('aria-current', 'true');
      else links[i].removeAttribute('aria-current');
    }
  }

  function syncToggles(section) {
    var toggles = section.querySelectorAll(GRID_TOGGLE);
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-pressed', toggles[i].classList.contains(TOGGLE_ACTIVE) ? 'true' : 'false');
    }
  }

  function init(section) {
    if (!section || section.__tcCollectionStatus) return;
    var grid = section.querySelector(GRID);
    if (!grid) return;
    section.__tcCollectionStatus = true;

    var state = { section: section, grid: grid, trigger: null, prior: null, inflight: 0, timer: 0, retry: 0, announceTimer: 0, pendingAnnounce: null };

    // Search page merge finished (assets/tc-search.js): announce the message held back above.
    section.addEventListener('tc:results:ready', function () {
      var pending = state.pendingAnnounce;
      if (!pending) return;
      state.pendingAnnounce = null;
      announce(state, pending.trigger);
    });

    // What started the next update: a sort option, a chip / Clear / pagination link, or a
    // control in the filter panel (checkboxes and price fields fire input).
    section.addEventListener('click', function (evt) {
      var el = evt.target && evt.target.closest ? evt.target.closest(SORT_LINK + ',' + UPDATE_LINK) : null;
      if (!el || !section.contains(el)) return;
      record(state, el, el.matches(SORT_LINK) ? 'sort' : (el.closest(PAGINATION) ? 'page' : 'filter'));
    }, true);
    section.addEventListener('input', function (evt) {
      var sidebar = section.querySelector(SIDEBAR);
      if (sidebar && sidebar.contains(evt.target)) record(state, evt.target, 'filter', true);
    }, true);

    // theme.js marks the grid .is-loading as each request starts and replaces the grid's
    // children once per response. One observer, so starts and swaps are counted in order.
    // state.inflight is exactly the number of requests not yet answered: never pruned by age,
    // because a response can land after any delay (theme.js has no timeout) and a pruned slot
    // would let a late response use up a newer request's (Codex, PR #668). A request that never
    // answers only keeps later triggers from being retired early; TRIGGER_TTL still applies.
    new MutationObserver(function (records) {
      var swapped = false;
      for (var i = 0; i < records.length; i++) {
        if (records[i].type === 'childList') {
          if (state.inflight > 0) state.inflight--;
          swapped = true;
        } else if (startsRequest(records, i)) {
          state.inflight++;
          if (state.trigger) state.trigger.starts--;
        }
      }
      if (!swapped) return;
      state.prior = lastFocused;
      clearTimeout(state.timer);
      clearTimeout(state.retry); // a newer update supersedes a pending focus retry
      state.timer = setTimeout(function () { afterUpdate(state); }, SETTLE);
    }).observe(grid, { childList: true, attributes: true, attributeFilter: ['class'], attributeOldValue: true });

    var sortList = section.querySelector(SORT_LIST);
    if (sortList) {
      syncSort(section);
      new MutationObserver(function () { syncSort(section); })
        .observe(sortList, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    var toggles = section.querySelectorAll(GRID_TOGGLE);
    if (toggles.length) {
      syncToggles(section);
      var toggleObserver = new MutationObserver(function () { syncToggles(section); });
      for (var i = 0; i < toggles.length; i++) {
        toggleObserver.observe(toggles[i], { attributes: true, attributeFilter: ['class'] });
      }
    }
  }

  function initAll(root) {
    var scope = root || document;
    if (scope.matches && scope.matches(SECTION)) init(scope);
    var sections = scope.querySelectorAll(SECTION);
    for (var i = 0; i < sections.length; i++) init(sections[i]);
  }

  // Theme editor: a re-rendered section brings a fresh container.
  document.addEventListener('shopify:section:load', function (evt) { initAll(evt.target); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
